import { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { get, kebabCase } from "lodash"
import { StarRounded, OpenInNew, KeyboardArrowRight, InfoOutlined, OfflineBolt } from "@mui/icons-material"
import { TableRow, TableCell } from "@mui/material"
import OPTooltip from "components/material/tooltip"
import { computeDrift, getDriftType } from "helpers/drift"
import { getFactorDisabled } from "helpers/factors"
import Drift from "components/drift/drift"
import StringDrift from "components/drift/stringDrift"
import { FORMATS, makeCustomFormatter, percentageFormatter } from "helpers/formatters/numbers"
import ThresholdIcon from "components/threshold-icon"
import { THRESHOLD_PARAMS, computeThresholdIcon } from "helpers/threshold"

export default function FactorsTableRow({
  contributor,
  show,
  openFactorProfile: factorProfileAction,
  factorLink,
  favorite,
  itemRow,
  disabled,
  drift,
  driftType,
  driftArrow,
  showDriftColumn,
  noPercent,
  noFactorDrilldown,
  thresholdParams,
  formatter: customFormatter,
}) {
  const defaultNumberFormatter = makeCustomFormatter(FORMATS.NUMBER_FORMAT, { emptyValueFiller: "-" })
  const openFactorProfile = useCallback(() => factorProfileAction && factorProfileAction(contributor.id), [contributor.id, factorProfileAction])

  const showContributorDetails = useCallback(() => {
    if (!disabled) {
      show(contributor)
    }
  }, [contributor, disabled, show])

  const noDrilldownClick = useCallback(() => {
    if (noFactorDrilldown) {
      openFactorProfile()
    }
  }, [noFactorDrilldown, openFactorProfile])

  const contributorHeader = useMemo(() => {
    const tooltip = contributor.tooltip ? <OPTooltip title={contributor.tooltip} className="contributor-tooltip">
      <InfoOutlined fontSize="small" className="contributor-tooltip-icon" />
    </OPTooltip> : null

    const contributorLabel = contributor.categoryLabel || contributor.label

    return <div className="factor-link-wrapper" onClick={showContributorDetails}>
      <span data-test-class={`${kebabCase(contributorLabel)}-label`}>
        {contributor.adjustedReason && <OPTooltip title={contributor.adjustedReason}><OfflineBolt fontSize="small" className="contributor-adjusted-icon" /></OPTooltip>}
        <span className="contributor-label">{contributorLabel}</span>
        {tooltip}
      </span>
      <KeyboardArrowRight />
    </div>
  }, [contributor, showContributorDetails])

  const value = useMemo(() => {
    if (customFormatter) {
      return customFormatter(contributor.value)
    }
    if (noPercent) {
      return defaultNumberFormatter(contributor.value)
    }
    return defaultNumberFormatter(contributor.value * 100)
  }, [customFormatter, noPercent, defaultNumberFormatter, contributor.value])

  const showFactorProfile = useMemo(() => {
    return !getFactorDisabled(contributor.category) && !contributor.isAggregated
  }, [contributor])

  const computedDrift = useMemo(() => {
    if (!drift || !contributor) {
      return null
    }
    return computeDrift(drift.mean, drift.stdDev, contributor.value)
  }, [contributor, drift])

  const content = useMemo(() => {
    const isThreshold = !!thresholdParams
    let thresholdIcon
    if (isThreshold) {
      thresholdIcon = computeThresholdIcon(contributor.value, thresholdParams)
    }
    if (drift || driftArrow || thresholdIcon) {
      let title, icon
      if (driftArrow) {
        title = `${contributor.categoryLabel || contributor.label} is ${driftArrow}`
        icon = <StringDrift drift={driftArrow} />
      } else if (thresholdIcon) {
        title = thresholdIcon.tooltip
        icon = <ThresholdIcon threshold={thresholdIcon.icon} />
      } else {
        title = `${driftType} drift for ${contributor.categoryLabel || contributor.label} is ${get(getDriftType(computedDrift), "label")}`
        icon = <Drift drift={computedDrift} />
      }

      return <OPTooltip
        placement="bottom-end"
        classes={{ popper: "analyze-contributors-drift", tooltip: "drift-tooltip" }}
        title={title}
      >
        <div className="factor-value-wrapper">
          {icon}
          {value}
        </div>
      </OPTooltip>
    }

    return <div className="factor-value-wrapper"><span className={`components_${isThreshold ? "threshold" : "drift"}-icon`} />{value}</div>
  }, [thresholdParams, drift, driftArrow, value, contributor.value, contributor.categoryLabel, contributor.label, driftType, computedDrift])

  return <TableRow className={classNames("detail-row factor", contributor.id, { favorite, disabled })}>
    <TableCell component="th" scope="row" className="name" title={contributor.tooltip ? "" : contributor.label}>
      <div className="factor-link" style={{ color: itemRow ? contributor.color : "initial" }} onClick={noDrilldownClick}>
        <StarRounded className="star-icon" />
        {showFactorProfile && factorLink && <OpenInNew onClick={() => openFactorProfile()} className="factor-icon" />}
        {contributorHeader}
      </div>
    </TableCell>
    <TableCell align="right" style={{ color: itemRow ? contributor.color : "initial" }}>
      {content}
    </TableCell>
    {showDriftColumn && drift && <TableCell align="right">{percentageFormatter(drift.change)}</TableCell>}
  </TableRow>
}

FactorsTableRow.propTypes = {
  contributor: PropTypes.object.isRequired,
  show: PropTypes.func,
  openFactorProfile: PropTypes.func,
  driftType: PropTypes.string,
  factorLink: PropTypes.bool,
  favorite: PropTypes.bool,
  itemRow: PropTypes.bool,
  disabled: PropTypes.bool,
  drift: PropTypes.object,
  noPercent: PropTypes.bool,
  noFactorDrilldown: PropTypes.bool,
  driftArrow: PropTypes.string,
  thresholdParams: THRESHOLD_PARAMS,
  formatter: PropTypes.func,
  showDriftColumn: PropTypes.bool,
}
